import "firebase/auth";

import * as firebase from "firebase/app";
import Vue from "vue";
import { GetterTree, Module } from "vuex";

import { OwnersAPI } from "@/api/owners";
import { PatientsApi } from "@/api/patients";
import { PractitionersAPI } from "@/api/practitioners";
import { RegistrarsAPI } from "@/api/registrars";
import { RootState } from "@/types";

import { ProfileSettingsState } from "./interfaces/profileSettingsState";
import { state } from "./state";

export const profileSettings: Module<ProfileSettingsState, RootState> = {
  namespaced: true,
  state,
  mutations: {
    setAvatar(state, payload) {
      state.avatar = payload;
    },
    setFullOwnerProfile(state, payload) {
      state.ownerSettings = payload;
    },
    setFullRegistrarProfile(state, payload) {
      state.registrarSettings = payload;
    },
    setFullPractitionerProfile(state, payload) {
      state.practitionerSettings = payload;
    },
    setFullPatientProfile(state, payload) {
      state.patientSettings = payload;
    },
    setMedicalInformation(state, payload) {
      const { value, fieldName } = payload;
      Vue.set(state.patientSettings.medicalInformation, fieldName, value);
    },
  },
  actions: {
    async resetPassword({ rootGetters, dispatch }, { oldPassword, newPassword }) {
      const { email, rememberMe } = rootGetters;
      await dispatch("submitLogin", { email, rememberMe, password: oldPassword }, { root: true });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      await firebase.auth().currentUser!.updatePassword(newPassword);
    },
    async uploadAvatar({ rootGetters, getters, commit }) {
      const { avatar } = getters;
      if (!avatar) return null;
      const { uid, role } = rootGetters;
      const data = new FormData();
      data.append("file", avatar);
      let avatarLink;
      if (role === "practitioner") {
        avatarLink = await PractitionersAPI.avatarUpload(uid, data);
      } else {
        avatarLink = await PatientsApi.avatarUpload(uid, data);
      }
      await this.dispatch("actualizeUserInfo", { root: true });
      commit("setPhotoURL", avatarLink, { root: true });
    },
    async getPractitionerSettings({ rootGetters, commit }) {
      const { uid } = rootGetters;
      const [settings, profile] = await Promise.all([
        PractitionersAPI.practitionerGetSettings(uid),
        PractitionersAPI.getPractitionerById(uid),
      ]);

      commit("setFullPractitionerProfile", {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        timeZone: settings.timeZone,
        locale: settings.locale,
        requestsNotAllowedEarlierThan: settings.requestsNotAllowedEarlierThan,
        canRequestNoLaterThan: settings.canRequestNoLaterThan,
        visitCanceledEarlierThan: settings.visitCanceledEarlierThan,
      });
    },
    async getOwnerSettings({ rootGetters, commit }) {
      const { uid } = rootGetters;
      const [settings, profile] = await Promise.all([OwnersAPI.ownerGetSettings(uid), OwnersAPI.getOwnerById(uid)]);

      commit("setFullOwnerProfile", {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        timeZone: settings.timeZone,
        locale: settings.locale,
      });
    },
    async getRegistrarSettings({ rootGetters, commit }) {
      const { uid } = rootGetters;
      const [settings, profile] = await Promise.all([
        RegistrarsAPI.registrarGetSettings(uid),
        RegistrarsAPI.getRegistrarById(uid),
      ]);

      commit("setFullRegistrarProfile", {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        timeZone: settings.timeZone,
        locale: settings.locale,
      });
    },
    async getPatientSettings({ rootGetters, commit }) {
      const { uid } = rootGetters;
      const profile = await PatientsApi.patientGetProfile(uid);
      const settings = await PatientsApi.getSettings(uid);
      const { medicalInformation, generalInformation, locale } = settings;
      commit("setFullPatientProfile", {
        ...state.patientSettings,
        firstName: profile.firstName,
        lastName: profile.lastName,
        birthDate: profile.birthDate,
        email: profile.email,
        state: profile.state,
        address: profile.address,
        homePhoneNumber: profile.homePhoneNumber,
        phoneNumber: profile.phoneNumber,
        ...(medicalInformation ? { medicalInformation } : {}),
        ...(generalInformation ? { generalInformation } : {}),
        locale,
      });
    },
    async saveOwnerChanges({ rootGetters, dispatch, commit }, settings) {
      const { uid } = rootGetters;
      const { firstName, lastName, locale } = settings;

      await OwnersAPI.updateProfile(uid, {
        name: { firstName, lastName },
      });
      await OwnersAPI.ownerUpdateSettings(uid, {
        locale,
      });
      await dispatch("uploadAvatar");

      commit("setFullOwnerProfile", {
        ...settings,
      });
    },
    async saveRegistrarChanges({ rootGetters, dispatch, commit }, settings) {
      const { uid } = rootGetters;
      const { firstName, lastName, locale } = settings;

      await RegistrarsAPI.updateProfile(uid, {
        name: { firstName, lastName },
      });
      await RegistrarsAPI.registrarUpdateSettings(uid, {
        locale,
      });
      await dispatch("uploadAvatar");

      commit("setFullRegistrarProfile", {
        ...settings,
      });
    },
    async savePractitionerChanges({ rootGetters, dispatch, commit }, settings) {
      const { uid } = rootGetters;
      const {
        firstName,
        lastName,
        locale,
        timeZone,
        requestsNotAllowedEarlierThan,
        canRequestNoLaterThan,
        visitCanceledEarlierThan,
      } = settings;

      await PractitionersAPI.updateProfile(uid, {
        name: { firstName, lastName },
      });
      await PractitionersAPI.practitionerUpdateSettings(uid, {
        locale,
        timeZone,
        requestsNotAllowedEarlierThan,
        canRequestNoLaterThan,
        visitCanceledEarlierThan,
      });
      await dispatch("uploadAvatar");

      commit("setFullPractitionerProfile", {
        ...settings,
      });
    },
    async savePatientChanges({ rootGetters, getters, dispatch }, settings) {
      const { uid } = rootGetters;
      const { medicalInformation } = getters;
      const { profile, generalInformation, locale } = settings;
      await PatientsApi.updateProfile(uid, profile);
      await PatientsApi.updateSettings(uid, { medicalInformation, generalInformation, locale });
      await dispatch("uploadAvatar");
    },
  },
  getters: {
    ...Object.keys(state).reduce((acc: GetterTree<ProfileSettingsState, RootState>, fieldName: string) => {
      acc[fieldName] = (state: ProfileSettingsState) => state[fieldName];
      return acc;
    }, {}),
    medicalInformation: (state) => state.patientSettings.medicalInformation,
    generalInformation: (state) => state.patientSettings.generalInformation,
    locale: (state) => state.practitionerSettings?.locale || state.ownerSettings.locale,
  },
};
